/**
 * MarginGuide — what this page LEAVES OUT, for the entity you are standing on.
 *
 * ⭐ CUT FROM 848 WORDS TO FOUR BULLETS (Mike, 24 Aug 2026), because the surface overtook it.
 * It explained "two lenses", the cost basis per site, the coverage percentages, the Sage bridge
 * and its own worked arithmetic — and every one of those is now ON the page, derived from
 * served data: Sage sits under each card, the coverage band names the basis per entity, the
 * reconciliation table closes the strip, and the Sage → ERP panel does it month by month.
 *
 * A note that repeats the screen is a second place for the same fact to go stale, and this one
 * had already done it twice: it taught an Actual / Standard toggle months after the toggle was
 * deleted, and quoted a 27.4-point Oracle gap the de-load had closed to 4.4. Both were on
 * screen while the surface eight pixels below said otherwise.
 *
 * ⚠ AND EVERY BULLET IS TAGGED BY SITE (Mike again, same day). Three of the four are
 * FactoryMaster facts — the cancelled documents, the capital sale, the job-cost cap — and at
 * Oracle they described a system the page was not reading. This is the fourth place on this
 * surface that fixed static copy naming the wrong entity. Copy cannot follow the scope; a
 * `sites` tag can.
 *
 * ⚠ NO FIGURE FROM OUTSIDE THE SCOPED WINDOW. The cap bullet used to quantify itself with
 * "removed 34.3% of the 2025 cost" on a page showing 2026 — evidence for a claim about data
 * the reader cannot see. The measurement lives in the spec; the bullet states the rule.
 *
 * ⚠ BEFORE ADDING ANYTHING HERE, check the page does not already say it. A derived figure beats
 * a written one every time: the card subtitles COMPUTE the Sage comparison this note used to
 * hardcode, so they cannot drift. Full definitions live on the Definitions surface; the
 * measured history is in specs/history/cost-definitions-standard-vs-actual.md.
 */

import React from 'react'
import type { Site } from '../types/metrics'

const TERM = 'font-semibold text-ink'

/** `sites` omitted means it is true everywhere. Otherwise it is a property of THAT system —
 * FactoryMaster at Kirkstall, Red Ant at Oracle — and Group sees all of them. */
interface Bullet {
  id: string
  sites?: Site[]
  body: React.ReactNode
}

const BULLETS: Bullet[] = [
  {
    id: 'unbilled',
    body: (
      <>
        <span className={TERM}>Work not yet billed.</span> A margin needs a finished job, and an
        unbilled line has only a quote against it. Forward work is the order book — a different
        question, with its own surface.
      </>
    ),
  },
  {
    id: 'cancelled',
    sites: ['Kirkstall'],
    body: (
      <>
        <span className={TERM}>48 cancelled documents and 68 cancelled lines.</span> A credit note
        is not one of them: it carries a negative value and we sum it, never negate it, because
        negating a credit adds it back into revenue.
      </>
    ),
  },
  {
    id: 'capital',
    sites: ['Kirkstall'],
    body: (
      <>
        <span className={TERM}>One capital sale that Sage does not count.</span> Invoice{' '}
        <span className="font-mono text-[11px] text-ink">00004872</span> at £81,950 is in these
        figures and excluded from the ledger. It is the whole reason June 2026 reads high against
        Sage, and it is not an error on either side.
      </>
    ),
  },
  {
    id: 'cap',
    sites: ['Kirkstall'],
    body: (
      <>
        <span className={TERM}>Cost above what the job actually took.</span> The job cost is capped
        at that works order&apos;s whole cost. The cap is not optional — a unit-of-measure mismatch
        otherwise draws hundreds of times the real figure.
      </>
    ),
  },
  {
    id: 'account',
    sites: ['Oracle'],
    body: (
      <>
        <span className={TERM}>No customer account code at Oracle.</span> Red Ant records none
        against a delivery. The customer is reached through the sales order instead, so the axis
        names almost every line and holds the rest in one bucket called (not recorded).
      </>
    ),
  },
]

export default function MarginGuide({ site }: { site: Site }): React.JSX.Element {
  const shown = BULLETS.filter((b) => !b.sites || site === 'Group' || b.sites.includes(site))
  return (
    <details className="border border-line rounded-xl bg-panel overflow-hidden">
      <summary className="px-4 py-3 cursor-pointer select-none text-[13.5px] font-semibold text-ink">
        What this page leaves out
        <span className="ml-2 font-mono text-[10px] text-mut2 font-normal">— the rows you cannot see</span>
      </summary>
      <ul className="space-y-2 border-t border-line2 px-4 pb-4 pt-3 text-[12.5px] leading-relaxed text-mut">
        {shown.map((b) => (
          <li key={b.id}>{b.body}</li>
        ))}
      </ul>
    </details>
  )
}
